import { styled } from '@mui/material/styles';
import { Paper, Typography, Box, Tooltip, IconButton } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import {
  Program,
  ServicePlan,
  ServicePlanServiceArea,
} from '../../../data/models/client-program.model';
import ProgramList from './ProgramList';
import { Client } from '../../../data/models/client.model';
import { TransportationBenefit } from '../../../data/models/transportation-benefit.model';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { ClientPurposeCode } from '../../../data/models/purpose-code-model.model';

const PREFIX = 'ClientPrograms';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    minWidth: 400,
  },

  [`& .${classes.details}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontSize: 21,
    fontWeight: 700,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

interface ClientProgramsProps {
  client?: Client;
  programs: Program[];
  servicePlans: ServicePlan[];
  purposeCodes: ClientPurposeCode[];
  serviceAreas: ServicePlanServiceArea[];
  transportationBenefits: TransportationBenefit[];
  addProgram: () => void;
  onEdit: (evt: any, program: Program) => void;
  onActivate: (evt: any, program: Program) => void;
  onDeactivate: (evt: any, program: Program) => void;
  onDelete: (evt: any, program: Program) => void;
  onDragEnter: (evt: any, program: Program) => void;
  onDragLeave: (evt: any, program: Program) => void;
  onDrop: (program: Program) => void;
  dropProgramUUID?: string;
}

export default function ClientPrograms({
  client,
  programs,
  servicePlans,
  purposeCodes,
  serviceAreas,
  transportationBenefits,
  addProgram,
  onEdit,
  onActivate,
  onDeactivate,
  onDelete,
  onDragEnter,
  onDragLeave,
  onDrop,
  dropProgramUUID,
}: ClientProgramsProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.details}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {t('client.programs')}
          </Typography>
          {!isSuperReaderUser && (
            <Tooltip title={t('client.addProgram')}>
              <span>
                <IconButton
                  data-testid="add-program"
                  onClick={addProgram}
                  color="primary"
                  size="large">
                  <AddCircleOutlineOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        {client && (
          <ProgramList
            client={client}
            programs={programs}
            servicePlans={servicePlans}
            purposeCodes={purposeCodes}
            serviceAreas={serviceAreas}
            transportationBenefits={transportationBenefits}
            onEdit={onEdit}
            onDelete={onDelete}
            onActivate={onActivate}
            onDeactivate={onDeactivate}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            dropProgramUUID={dropProgramUUID}
          />
        )}
      </Paper>
    </StyledBox>
  );
}
